/**
 * Alpine.js Tabs Directive
 *
 * This directive provides a simple and accessible way to create tabbed interfaces with Alpine.js.
 * It handles state management and URL synchronization
 *
 * Usage:
 *
 * ```html
 * <.app_page x-data="{ activeTab: 'tab-a' }" x-page-tabs>
 *   <:content_switcher>
 *     <.content_switcher_button data-tabs-button="info">{gettext("Informations")}</.content_switcher_button>
 *     <.content_switcher_button data-tabs-button="sessions">{gettext("Sessions")}</.content_switcher_button>
 *     <.content_switcher_button data-tabs-button="emails">{gettext("Suivi des e-mails")}</.content_switcher_button>
 *   </:content_switcher>
 *
 *   <div x-show="activeTab === 'info'">Content for Tab A</div>
 *   <div x-show="activeTab === 'sessions'">Content for Tab B</div>
 *   <div x-show="activeTab === 'emails'">Content for Tab C</div>
 * </app_page>
 * ```
 *
 * Note: this for now is tied to the content switcher component, but we also have tabs components that may appear multiple times on the
 * same page. If you wish to use this directive with the tabs component, please ask around (Antoine, Zenzo) about the best course of
 * action.
 */

// Helper function to determine the initial active tab
function determineInitialTab($data, el, considerUrl = true) {
  if (considerUrl) {
    const url = new URL(window.location);
    const urlTab = url.searchParams.get("tab");
    if (urlTab) {
      return urlTab; // URL parameter takes highest precedence
    }
  }

  // If URL param not present, check x-data (already assigned to initialTab)
  if ($data?.activeTab) {
    return $data.activeTab;
  }

  // Fallback to the first button found
  const firstButton = el.querySelector("[data-tabs-button]");
  if (firstButton) {
    return firstButton.dataset.tabsButton;
  }

  throw new Error("No initial tab found. Please provide a default tab or ensure buttons are defined.");
}

export function initForAlpine(Alpine) {
  Alpine.directive("page-tabs", (el, { modifiers }, { effect, cleanup }) => {
    const $data = Alpine.$data(el);
    if (!$data.$root) {
      throw new Error("x-page-tabs directive requires a component scope (add x-data)");
    }

    const handleUrl = !modifiers.includes("no-url");

    const buttons = Array.from(el.querySelectorAll("[data-tabs-button]"));
    const buttonClickListeners = [];

    // Determine and set initial state
    const initialTab = determineInitialTab($data, el, handleUrl);

    // Set activeTab in component state if not already defined, or if initial logic dictates override
    if (typeof $data.activeTab === "undefined") {
      $data.activeTab = initialTab;
    }

    // Initial activation
    el.dispatchEvent(new CustomEvent("tab-change", { detail: { tab: initialTab }, bubbles: true, composed: true }));

    // Define setActiveTab method on the component scope if not already present
    if (typeof $data.setActiveTab === "undefined") {
      $data.setActiveTab = function (tabName, updateUrl = true) {
        if (this.activeTab === tabName) return;

        this.activeTab = tabName;

        if (handleUrl && updateUrl) {
          const url = new URL(window.location);
          url.searchParams.set("tab", tabName);
          window.history.pushState(null, "", url.toString());
        }

        document.querySelector(".app-page__content").scrollTo(0, 0);

        el.dispatchEvent(new CustomEvent("tab-change", { detail: { tab: tabName }, bubbles: true, composed: true }));
      };
    }

    // Popstate listener for browser navigation
    const handlePopState = (_event) => {
      const url = new URL(window.location);
      const newTab = url.searchParams.get("tab") || buttons[0]?.dataset.tabsButton || null;
      if (newTab !== null && $data.activeTab !== newTab) {
        $data.setActiveTab(newTab, false); // Update state without pushing history
      }
    };
    handleUrl && window.addEventListener("popstate", handlePopState);

    // Button setup
    buttons.forEach((button) => {
      const tabName = button.dataset.tabsButton;
      if (!tabName) return;
      button.setAttribute("role", "tab");

      const onClick = () => {
        $data.setActiveTab(tabName, true);
      };
      button.addEventListener("click", onClick);
      buttonClickListeners.push({ element: button, handler: onClick });
    });

    // Reactive effect for updates
    effect(() => {
      const currentActiveTab = $data.activeTab;

      buttons.forEach((button) => {
        const tabName = button.dataset.tabsButton;
        const isActive = currentActiveTab === tabName;
        button.classList.toggle("button--active", isActive);
        button.setAttribute("aria-selected", isActive ? "true" : "false");
        button.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    });

    // Initial URL synchronization
    if (handleUrl) {
      const currentUrlTab = new URL(window.location).searchParams.get("tab");
      if ($data.activeTab && currentUrlTab !== $data.activeTab) {
        const url = new URL(window.location);
        url.searchParams.set("tab", $data.activeTab);
        window.history.replaceState({ tab: $data.activeTab }, "", url.toString());
      } else if (!$data.activeTab && currentUrlTab) {
        const url = new URL(window.location);
        url.searchParams.delete("tab");
        window.history.replaceState({}, "", url.toString());
      }
    }

    // Subscribe to outside commands
    const handler = (event) => {
      const newTab = event.detail.tab;
      if (newTab && newTab !== $data.activeTab) {
        $data.setActiveTab(newTab, event.detail.updateUrl);
      }
    };
    window.addEventListener("switch-tab", handler);

    // Cleanup
    cleanup(() => {
      handleUrl && window.removeEventListener("popstate", handlePopState);
      buttonClickListeners.forEach(({ element, handler }) => {
        element.removeEventListener("click", handler);
      });
      window.removeEventListener("switch-tab", handler);
    });
  });
}

export const switchTo = (tab) => {
  const event = new CustomEvent("switch-tab", {
    detail: {
      tab: tab,
      updateUrl: false,
    },
  });
  window.dispatchEvent(event);
};
